import { computed, defineComponent, h } from 'vue';

import { classNames } from '@components/utils';

import { useInjectTable } from '../../context';

import HeaderRow from './HeaderRow';

import type { NTable } from '../../interface';

export interface IHeaderProps<RecordType = NTable.TDefaultRecord> {
  columns: NTable.NColumn.TColumns<RecordType>;
  flattenColumns: readonly NTable.NColumn.IColumn<RecordType>[];
  stickyOffsets: NTable.NSticky.IStickyOffsets;
  customHeaderRow: NTable.NCustomize.TGetComponentProps<readonly NTable.NColumn.IColumn<RecordType>[]> | any;
}

export default defineComponent<IHeaderProps>({
  // eslint-disable-next-line vue/no-reserved-component-names
  name: 'Header',
  inheritAttrs: false,
  props: ['columns', 'flattenColumns', 'stickyOffsets', 'customHeaderRow'] as any,
  setup(props) {
    const tableContext = useInjectTable();
    const rows = computed(() => parseHeaderRows(props.columns));

    return () => {
      const { getComponent } = tableContext;
      const { stickyOffsets, flattenColumns, customHeaderRow } = props;

      const WrapperComponent = getComponent(['header', 'wrapper'], 'thead');
      const trComponent = getComponent(['header', 'row'], 'tr');
      const thComponent = getComponent(['header', 'cell'], 'th');

      return h(
        WrapperComponent,
        { class: 'cds-table__thead' },
        rows.value.map((row, rowIndex) =>
          h(HeaderRow, {
            key: rowIndex,
            index: rowIndex,
            cells: row,
            rowComponent: trComponent,
            cellComponent: thComponent,
            stickyOffsets,
            flattenColumns,
            customHeaderRow
          })
        )
      );
    };
  }
});

function parseHeaderRows<RecordType>(
  rootColumns: NTable.NColumn.TColumns<RecordType>
): NTable.NCell.ICell<RecordType>[][] {
  const rows: NTable.NCell.ICell<RecordType>[][] = [];

  // Generate `rows` cell data
  fillRowCells(rootColumns, 0);

  // Handle `rowSpan`
  const rowCount = rows.length;
  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    rows[rowIndex].forEach((cell) => {
      if (!('rowSpan' in cell) && !cell.hasSubColumns) {
        cell.rowSpan = rowCount - rowIndex;
      }
    });
  }

  function fillRowCells(columns: NTable.NColumn.TColumns<RecordType>, colIndex: number, rowIndex = 0): number[] {
    rows[rowIndex] = rows[rowIndex] || [];

    let currentColIndex = colIndex;
    return columns.filter(Boolean).map((column) => {
      const cell: NTable.NCell.ICell<RecordType> = {
        key: column.key,
        column,
        colStart: currentColIndex,
        class: classNames(column.className, column.class)
      };

      let colSpan: number = 1;

      const subColumns = (column as NTable.NColumn.IColumnGroup<RecordType>).children;
      if (subColumns && subColumns.length > 0) {
        colSpan = fillRowCells(subColumns, currentColIndex, rowIndex + 1).reduce((total, count) => total + count, 0);
        cell.hasSubColumns = true;
      }

      if ('colSpan' in column) {
        colSpan = column.colSpan as number;
      }

      if ('rowSpan' in column) {
        cell.rowSpan = column.rowSpan;
      }

      cell.colSpan = colSpan;
      cell.colEnd = (cell.colStart as number) + colSpan - 1;
      rows[rowIndex].push(cell);

      currentColIndex += colSpan;

      return colSpan;
    });
  }

  return rows;
}
